import styled, { css } from 'styled-components'

import { theme } from '../../constants'

export type RoleTone = keyof typeof theme.roleColors

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger'
export type ButtonSize = 'small' | 'medium' | 'large'

interface StyledButtonProps {
  $variant: ButtonVariant
  $size: ButtonSize
  $fullWidth: boolean
  $tone: RoleTone
}

const variantStyles = {
  primary: css<StyledButtonProps>`
    color: ${({ $tone }) => theme.roleColors[$tone].text};
    background: ${({ $tone }) => theme.roleColors[$tone].main};

    &:hover:not(:disabled) {
      background: ${({ $tone }) => theme.roleColors[$tone].hover};
    }
  `,
  secondary: css<StyledButtonProps>`
    color: ${({ $tone }) => theme.roleColors[$tone].strong};
    background: ${({ $tone }) => theme.roleColors[$tone].soft};

    &:hover:not(:disabled) {
      box-shadow: inset 0 0 0 1px ${({ $tone }) => theme.roleColors[$tone].main};
    }
  `,
  ghost: css<StyledButtonProps>`
    color: ${({ $tone }) => theme.roleColors[$tone].strong};
    background: transparent;

    &:hover:not(:disabled) {
      background: ${({ $tone }) => theme.roleColors[$tone].soft};
    }
  `,
  danger: css`
    color: ${theme.colors.white};
    background: ${theme.colors.error};

    &:hover:not(:disabled) {
      background: #b93636;
    }
  `,
}

const sizeStyles = {
  small: css`
    min-height: ${theme.layout.controlHeight};
    padding: 8px 14px;
    font-size: ${theme.typography.size.bodySmall};
  `,
  medium: css`
    min-height: ${theme.layout.controlHeight};
    padding: 10px 18px;
    font-size: ${theme.typography.size.bodySmall};
  `,
  large: css`
    min-height: ${theme.layout.controlHeightLarge};
    padding: 12px 22px;
    font-size: ${theme.typography.size.body};
  `,
}

export const StyledButton = styled.button<StyledButtonProps>`
  width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'auto')};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[2]};
  border: 0;
  border-radius: ${theme.radii.medium};
  font-family: ${theme.typography.family};
  font-weight: ${theme.typography.weight.semibold};
  line-height: ${theme.typography.lineHeight.compact};
  cursor: pointer;
  transition:
    background ${theme.motion.normal} ${theme.motion.easing},
    color ${theme.motion.normal} ${theme.motion.easing},
    transform ${theme.motion.fast} ${theme.motion.easing},
    box-shadow ${theme.motion.fast} ${theme.motion.easing};

  ${({ $variant }) => variantStyles[$variant]}
  ${({ $size }) => sizeStyles[$size]}

  &:active:not(:disabled) {
    transform: translateY(1px) scale(0.99);
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px ${({ $tone }) => theme.roleColors[$tone].focus};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.48;
  }

  svg {
    flex: 0 0 auto;
    font-size: 1.15em;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`
